import { createContext, useContext, useState, type ReactNode } from 'react';
import {
  otherMode,
  readReportMode,
  writeReportMode,
  type PortalReportMode,
} from '../../../lib/portalReport/reportTheme';

// CLIENT PORTAL — light/dark, ONE STATE FOR THE WHOLE PORTAL.
//
// ⚠️⚠️ THE DEFECT THIS EXISTS TO PREVENT. The mode used to be owned by
// `RestyledReport`, which is one page of a portal. Once the SHELL took the
// report's look, a client who switched the report to light would have had a
// light report inside a dark shell — and worse, switching on the report page and
// then navigating home would have looked like the setting had been forgotten.
// One control, one state.
//
// ⚠️ THE CONTROL LIVES IN THE SHELL, and nowhere else in the portal. The report
// no longer renders one: two controls for one value is how they end up
// disagreeing about which is authoritative.
//
// ⚠️ THE STORAGE KEY IS UNCHANGED (`REPORT_MODE_STORAGE_KEY`). A client who
// already chose light keeps it — renaming would silently reset everyone's
// preference for no reason, and unlike the restyle flag there is no stale value
// here that means something different now.
//
// ⚠️ A LAZY INITIALISER, not an effect — an effect would paint the default first
// and then swap, flashing a dark portal at a client who chose light. Lifted from
// `RestyledReport`'s own `useReportMode`, which is now gone.
//
// ⚠️ Every localStorage access is wrapped inside `readReportMode` /
// `writeReportMode`: some privacy modes THROW rather than returning null, and an
// unhandled throw would white-screen the portal for somebody whose only mistake
// was browsing privately.

export type PortalModeValue = {
  mode: PortalReportMode;
  toggle: () => void;
};

/**
 * ⚠️ The DEFAULT here is a real, working value rather than a throw. A component
 * rendered outside the provider — the staff "view as client" preview reuses
 * these components — gets dark and an inert toggle rather than a crash. The
 * preview supplies its own mode explicitly; see `RestyledReport`.
 */
const PortalModeContext = createContext<PortalModeValue>({
  mode: 'dark',
  toggle: () => undefined,
});

export function PortalModeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<PortalReportMode>(() =>
    readReportMode(typeof window === 'undefined' ? null : window.localStorage),
  );
  const toggle = (): void => {
    setMode((current) => {
      const next = otherMode(current);
      writeReportMode(typeof window === 'undefined' ? null : window.localStorage, next);
      return next;
    });
  };
  return (
    <PortalModeContext.Provider value={{ mode, toggle }}>{children}</PortalModeContext.Provider>
  );
}

export function usePortalMode(): PortalModeValue {
  return useContext(PortalModeContext);
}
